import type { AdminSupportInquiry, AdminSupportInquiryPage } from '@inflip/contracts/admin';
import {
  SUPPORT_INQUIRY_CATEGORY_LABELS,
  SUPPORT_INQUIRY_STATUS_LABELS,
  SupportInquiryCategory,
  SupportInquiryStatus,
} from '@inflip/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

import { DataTable } from '../components/data-table';
import { Select } from '../components/select';
import {
  Badge,
  type BadgeTone,
  Button,
  Chip,
  EmptyState,
  ErrorBanner,
  inputCls,
  LoadingRow,
  PageHeader,
  Pager,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';

const PAGE_SIZE = 20;

export const SUPPORT_STATUS_TONE: Record<SupportInquiryStatus, BadgeTone> = {
  WAITING: 'red',
  ANSWERED: 'green',
  CLOSED: 'default',
};

const STATUS_CHIPS: { key: SupportInquiryStatus | ''; label: string }[] = [
  { key: SupportInquiryStatus.WAITING, label: '답변 대기' },
  { key: SupportInquiryStatus.ANSWERED, label: '답변 완료' },
  { key: SupportInquiryStatus.CLOSED, label: '종료' },
  { key: '', label: '전체' },
];

const COLUMNS: ColumnDef<AdminSupportInquiry>[] = [
  {
    id: 'status',
    header: '상태',
    cell: ({ row }) => (
      <span className="flex flex-wrap items-center gap-1">
        <Badge tone={SUPPORT_STATUS_TONE[row.original.status]}>
          {SUPPORT_INQUIRY_STATUS_LABELS[row.original.status]}
        </Badge>
        {/* 회원이 지운 문의 — 회원 화면에서는 사라졌고 함께 종료됐다 */}
        {row.original.userDeletedAt && <Badge>회원 삭제</Badge>}
      </span>
    ),
  },
  {
    id: 'category',
    header: '분류',
    cell: ({ row }) => SUPPORT_INQUIRY_CATEGORY_LABELS[row.original.category],
    meta: { tdClass: 'whitespace-nowrap text-xs' },
  },
  {
    id: 'title',
    header: '제목',
    cell: ({ row }) => (
      <Link
        to="/support/$inquiryId"
        params={{ inquiryId: row.original.id }}
        className="block truncate text-sm font-semibold hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {row.original.title}
      </Link>
    ),
  },
  {
    id: 'user',
    header: '회원',
    cell: ({ row }) => (
      <Link
        to="/users/$userId"
        params={{ userId: row.original.user.id }}
        className="block truncate text-xs hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {row.original.user.name ?? '(이름 없음)'}
        <span className="ml-1 text-muted-foreground">{row.original.user.email ?? ''}</span>
      </Link>
    ),
  },
  {
    id: 'order',
    header: '주문',
    cell: ({ row }) =>
      row.original.order ? (
        <Link
          to="/orders/$orderId"
          params={{ orderId: row.original.order.id }}
          className="block truncate text-xs text-brand hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {row.original.order.listingTitle ?? row.original.order.id.slice(0, 8)}
          <span className="ml-1 text-muted-foreground">
            ({row.original.order.role === 'buyer' ? '구매자' : '판매자'})
          </span>
        </Link>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
  },
  {
    id: 'lastMessageAt',
    header: '마지막 글',
    cell: ({ row }) => formatDateTime(row.original.lastMessageAt),
    meta: { tdClass: 'whitespace-nowrap text-xs text-muted-foreground' },
  },
];

/** 고객센터 1:1 문의 — 답변 대기 먼저(오래 기다린 순). 주문 상세에서 오면 그 주문의 문의만 */
export function SupportInquiriesPage() {
  const { session } = useAdminAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useListViewState('status', SupportInquiryStatus.WAITING as string);
  const [category, setCategory] = useListViewState('category', '');
  const [orderId, setOrderId] = useListViewState('orderId', '');
  const [searchTerm, setSearchTerm] = useListViewState('q', '');
  const [q, setQ] = useState(searchTerm);
  const [page, setPage] = useListViewState('page', 1);
  const [result, setResult] = useState<AdminSupportInquiryPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listSupportInquiries(session.accessToken, {
        page,
        pageSize: PAGE_SIZE,
        ...(status ? { status: status as SupportInquiryStatus } : {}),
        ...(category ? { category: category as SupportInquiryCategory } : {}),
        ...(orderId ? { orderId } : {}),
        ...(searchTerm ? { q: searchTerm } : {}),
      })
      .then((res) => !cancelled && setResult(res))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [session, status, category, orderId, searchTerm, page]);

  if (!session) return null;
  const rows = result?.items ?? [];

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="1:1 문의"
        description={
          result
            ? `${result.total.toLocaleString('ko-KR')}건`
            : '회원이 남긴 문의에 답하고 종료해요'
        }
        actions={
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearchTerm(q.trim());
            }}
          >
            <input
              className={`w-64 ${inputCls}`}
              placeholder="제목 / 회원 이름 / 이메일"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button type="submit">검색</Button>
          </form>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {STATUS_CHIPS.map((c) => (
          <Chip
            key={c.key || 'ALL'}
            active={status === c.key}
            onClick={() => {
              setPage(1);
              setStatus(c.key);
            }}
          >
            {c.label}
          </Chip>
        ))}
        {orderId && (
          <Chip
            active
            onClick={() => {
              setPage(1);
              setOrderId('');
            }}
          >
            주문 #{orderId.slice(0, 8)} ✕
          </Chip>
        )}
        <div className="ml-auto">
          <Select
            label="분류"
            className="w-44"
            appearance="filled"
            value={category}
            options={[
              { value: '', label: '분류 전체' },
              ...Object.values(SupportInquiryCategory).map((c) => ({
                value: c,
                label: SUPPORT_INQUIRY_CATEGORY_LABELS[c],
              })),
            ]}
            onValueChange={(v) => {
              setPage(1);
              setCategory(v);
            }}
          />
        </div>
      </div>

      {error && <ErrorBanner message={error} />}
      {loading && !result && <LoadingRow />}

      {result && rows.length === 0 ? (
        <EmptyState message="문의가 없어요." />
      ) : (
        <DataTable
          data={rows}
          columns={COLUMNS}
          getRowId={(r) => r.id}
          onRowClick={(r) =>
            void navigate({ to: '/support/$inquiryId', params: { inquiryId: r.id } })
          }
        />
      )}

      {result && (
        <Pager
          page={result.page}
          totalPages={result.totalPages}
          disabled={loading}
          onChange={setPage}
        />
      )}
    </main>
  );
}
